import { React, useEffect, useRef, useState } from "react";
import { Button, toast, AddIcon, UploadIcon, Card, Spinner, Collapsible, DownloadIcon, Tabs, Input, FormControl } from '@atlas-design-system/react';
import { JsonViewer } from '@textea/json-viewer';
import { fdEntryColumns } from '../utils/fdEntryColumns';
import AtlasTable from "./AtlasTable";
import { excludeColumns } from "../utils/excludeColumns";
import PccListInput from "../utils/PccListInput";
import { useFetchWrapper } from "../utils/useFetchWrapper";
//import { employtestees, testDataColumns } from "../utils/testData";

function Settings(props) {

    const inputFileRef = useRef(null);
    
    const [canUpload, setCanUpload] = useState(false);
    const [loading, setLoading] = useState(true);
    const [excelFile, setExcelFile] = useState();
    const [respJson, setRespJson] = useState('');
    const [pccList, setPccList] = useState([]);
    const [adHocScanAirlinesFilter, setAdHocScanAirlinesFilter] = useState('');

    const { settings: { initData: { excelHistory, lastScanDate, crone, swaggerUiUrl }, key: authKey } } = props;

    var myFetch = useFetchWrapper(authKey);

    useEffect(() => {
        myFetch.get("api/PccList").then(a => {
            setPccList(a);
        }).finally(() => setLoading(false));
    }, []);

    const onScanNow = () => {
        if (!adHocScanAirlinesFilter || adHocScanAirlinesFilter.split(';').every(a => a.length == 2)) {
            setLoading(true);
            const airlinesStr = adHocScanAirlinesFilter.split(';').join('-');
            myFetch.get(`api/AdHocHttpFunction?code=${authKey}&airlinesFilter=${airlinesStr}`).then(a => {
                if (a.StatusQueryGetUri) {
                    console.log(a);
                    toast.success('Scan started successfully', { position: 'top-right', duration: 6000 });
                } else {
                    toast.error('Scan failed. Check console for error details: ' + a, { position: 'top-right', duration: 5000 });
                }
            }).finally(() => setLoading(false));
        } else {
            toast.error('Airlines filter is not valid. Please provide airlines in correct format and try again.', { position: 'top-right', duration: 5000 });
        }
    }

    const onFileChange = event => {
        const firstFile = event.target.files[0];
        if (!firstFile)
            return;
        setExcelFile(firstFile);
        setCanUpload(true);
        inputFileRef.current.value = null;
    };

    const onLastExcelReportDownload = () => {
        var excelFileName = 'FdReport.xlsx';
        var url = `${process.env.REACT_APP_API_BASE_URL}api/ExcelUploadFunction?code=${authKey}&excelReportFileName=${excelFileName}`;
        downloadFile(url, excelFileName).then(_ => setLoading(false));
    }

    async function downloadFile(url, filename) {
        setLoading(true);
        const data = await fetch(url)
        const blob = await data.blob()
        const objectUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.setAttribute('href', objectUrl)
        link.setAttribute('download', filename)
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const handleErrors = (response) => {
        if (!response.ok) {
            console.log(response);
            throw Error(`${response.statusText}(${response.status})`);
        }
        return response.json();
    }

    const onFileUpload = () => {
        let formData = new FormData();
        formData.append("file", excelFile);
        setLoading(true);
        fetch(`${process.env.REACT_APP_API_BASE_URL}api/ExcelUploadFunction?code=${authKey}`,
            {
                mode: "cors",
                method: "post",
                body: formData
            })
            .then(handleErrors)
            .then(jsonData => {
                setLoading(false);
                setRespJson(jsonData);
                if (jsonData.data && jsonData.data.pccList) {
                    setCanUpload(false);
                    setExcelFile(null);
                    toast.success('File has been uploaded successfully', { position: 'top-right', duration: 5000 })
                    console.log(jsonData);
                } else {
                    toast.error('Excel file upload failed. Please check validation errors', { position: 'top-right', duration: 5000 });
                    console.log(jsonData);
                }
            }).catch(error => {
                setLoading(false);
                console.log(error);
                toast.error('Excel file upload failed. Check console for error details: ' + error, { position: 'top-right', duration: 5000 });
            });
    };

    const deleteFetchRequest = (record) => {
        var rk = record.rowKey;
        var pk = record.partitionKey;
        return myFetch.delete(`api/RemoveRecord?partitionKey=${pk}&rowKey=${rk}`)
            .then(a => {
                if (a) {
                    return Promise.resolve(record);
                }
                else {
                    return Promise.reject(`Error removing record from [${pk}] for rowKey: ${rk}`);
                }
            })
    }

    const loadFetchRequest = (entityName) => {
        return myFetch.get('api/GetRecordsFunction?entityName=' + entityName)
            .then(a => {
                if (a) {
                    return Promise.resolve(a);
                }
                return Promise.reject(a);
            })
    }

    const updateFetchRequest = (record) => {
        return myFetch.post('api/UpdateRecord?entityName=' + record.partitionKey, record).then(a => {
            if (a) {
                toast.success('Record updated successfully', { position: 'top-right', duration: 6000 });
                return Promise.resolve(a);
            }
            else {
                return Promise.reject(`Error updating record from [${record.partitionKey}] for rowKey: ${record.rowKey}`);
            }
        });
    }

    const addFetchRequest = (newRecord, entityName) => {
        newRecord.partitionKey = entityName;
        //setLoading(true)
        return myFetch.put('api/AddNewRecord?entityName=' + entityName, newRecord).then(a => {
            if (a && a.rowKey && a.partitionKey === entityName) {
                toast.success('Record added successfully', { position: 'top-right', duration: 6000 });
                return Promise.resolve(a);
            }
            else {
                return Promise.reject(`Error adding record to [${entityName}]`);
            }
        })//.finally(() => setLoading(false));
    }

    return (<>
        <Card id='Excel import' style={{ margin: 20, padding: 20 }}
            header={<h1>Fare display report - excel operations</h1>}>
            <div style={{ flexWrap: 'wrap', display: 'flex' }}>
                <Button className="atls-button btn-file"
                    data-appearance="secondary"
                    data-size="medium"
                    data-icon-align="left"
                    style={{ marginRight: 10, marginTop: 5 }}
                    icon={<UploadIcon />}
                    onClick={() => inputFileRef.current.click()}
                >
                    Browse excel file
                    <input ref={inputFileRef} type="file" onChange={onFileChange} style={{ display: 'none' }} />
                </Button>
                <div style={{ marginTop: 10, marginRight: 10 }}>
                    <p style={{ marginTop: 'auto', marginBottom: 'auto' }}>{excelFile?.name}</p>
                </div>
                <Button
                    data-appearance="primary"
                    data-icon-align="left"
                    data-size="medium"
                    disabled={!canUpload}
                    icon={<AddIcon />}
                    style={{ marginTop: 5 }}
                    onClick={onFileUpload}>
                    Upload excel with scan input data
                </Button>
            </div>

            <div style={{ marginTop: 10, display: 'flex' }}>
                <Button
                    data-appearance="primary"
                    data-icon-align="left"
                    data-size="medium"
                    style={{ marginTop: 'auto' }}
                    onClick={onScanNow} >
                    Scan now
                </Button>
                <FormControl label="Airlines to filter (separated by ';')" error=""
                    style={{ marginLeft: 10 }} >
                    <Input
                        value={adHocScanAirlinesFilter}
                        type="text"
                        placeholder="e.g. LO;LH;..."
                        onChange={e => setAdHocScanAirlinesFilter(e.target.value.toUpperCase())} />
                </FormControl>
            </div>

            <div style={{ marginTop: 10, marginBottom: 10, display: 'flex', flexWrap: 'wrap' }}>
                <h3 style={{ marginTop: 10, marginBottom: 0, marginRight: 10 }}>Last scan timestamp: {new Date(lastScanDate).toLocaleString()}
                </h3>
                <Button
                    data-appearance="primary"
                    data-icon-align="left"
                    data-size="medium"
                    style={{ marginTop: 5 }}
                    icon={<DownloadIcon />}
                    onClick={onLastExcelReportDownload}
                >Download last excel report</Button>
            </div>

            <div style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap' }}>
                <h3 style={{ marginTop: 10, marginBottom: 0, marginRight: 10 }}>Scheduler info: {crone}</h3>
            </div>

            <span><span style={{fontWeight: 'bold'}}>Swagger UI: </span><a href={swaggerUiUrl} rel="noreferrer" target='_blank'>{swaggerUiUrl}</a></span>

            {excelHistory && excelHistory.length > 0 && <Collapsible
                header="Show excel history" style={{ overflowX: 'auto', marginTop: 10 }}>
                <div className="sb-collapsible-content">
                    <ul style={{ display: 'inline-block', padding: 10, height: 150, overflowY: 'auto', listStyle: 'none' }}>{excelHistory.map((h, i) => <a key={i}
                        style={{ display: 'flex', width: 'fit-content' }}
                        href={`${process.env.REACT_APP_API_BASE_URL}api/ExcelUploadFunction?fileName=${h}&code=${authKey}`}
                        download><li>{h}</li>
                    </a>)}</ul>
                </div>
            </Collapsible>}

            <div style={{ marginTop: 20 }}>
                {respJson && !respJson.status && <JsonViewer rootName='responseJson'
                    displayDataTypes={false}
                    defaultInspectDepth={3}
                    indentWidth={4}
                    value={respJson} />}
            </div>
        </Card>
        {!loading && <Tabs style={{ marginLeft: 40, marginRight: 40, marginBottom: 40, overflowX: 'auto' }}
            defaultActiveKey="1"
            id="sb-tabs"
            onChange={function noRefCheck() { }}
        >
            <Tabs.Tab title="PCC list">
                <PccListInput authKey={authKey} tags={pccList} />
            </Tabs.Tab>
            <Tabs.Tab title="FD Entries">
                <AtlasTable loadRequest={() => loadFetchRequest('GdsEntry')} columns={fdEntryColumns}
                    deleteRequest={deleteFetchRequest} recordId="rowKey"
                    updateRequest={updateFetchRequest}
                    addRequest={(newRecord) => addFetchRequest(newRecord, 'GdsEntry')} />
            </Tabs.Tab>
            <Tabs.Tab title="Exclude rules">
                <AtlasTable loadRequest={() => loadFetchRequest('ExcludePcc')} columns={excludeColumns}
                    deleteRequest={deleteFetchRequest} recordId="rowKey"
                    updateRequest={updateFetchRequest}
                    addRequest={(newRecord) => addFetchRequest(newRecord, 'ExcludePcc')} />
            </Tabs.Tab>
            {/* <Tabs.Tab title="test">
                <AtlasTable loadRequest={() => {
                    return new Promise((resolve, _) => resolve(employtestees))

                }} columns={testDataColumns}
                    deleteRequest={(record) => new Promise((resolve, _) => resolve(record))} recordId="id"
                    updateRequest={(record) => new Promise((resolve, _) => resolve(true))} />
            </Tabs.Tab> */}
        </Tabs>}
        {loading && <Spinner overlay percent={50} primaryLabel="Please wait..." />}
    </>);
}

export default Settings;